import { Tutor } from '@/store/tutors/types';
import _ from 'lodash'
import axios from 'axios'
import moment from 'moment'
import { ActionTree } from 'vuex';
import { RootState } from '@/store/types';
import {
  Lesson,
  LessonsSearchConditions,
  LessonsState,
  Student,
} from '@/store/lessons/types';
import { scheduleType } from '@/store/schedules/types';

const actions: ActionTree<LessonsState, RootState> = {
  fetchLessons: (
    { commit, dispatch, getters },
    { date, tutor }: LessonsSearchConditions,
  ) => axios.get('/lessons', {
    params: _.pickBy({
      date: date?.format('YYYYMMDD'),
      tutorId: tutor?.id,
    }),
  }).then(async ({ data }) => {
    await Promise.all([
      dispatch('fetchMasterUniversities'),
      dispatch('fetchMasterCampuses'),
      dispatch('fetchMasterSemesters'),
      dispatch('fetchMasterLevels'),
    ])

    const lessons = _.map(
      data,
      (lesson: any): Lesson => ({
        scheduleId: lesson.lessonId,
        type: 'lesson',
        university: getters.findUniversityByCode(lesson.universityCode),
        campus: getters.findCampusByCode(lesson.campusCode),
        fiscalYear: lesson.fiscalYear,
        semester: getters.findSemesterByCode(lesson.semesterCode),
        date: moment(lesson.date, 'YYYYMMDD'),
        period: lesson.period,
        lessonMinute: null,
        unit: lesson.unit,
        booth: {
          building: lesson.boothBuilding,
          room: lesson.boothRoom,
          number: lesson.boothNumber,
        },
        course: {
          name: lesson.courseName,
        },
        level: getters.findLevelByCode(lesson.level),
        isApproved: lesson.isApproved,
        substituteTutor: lesson.substituteTutor,
        students: _.map(lesson.students, (s): { student: Student, wetecResult: string } => ({
          student: {
            id: s.studentId,
            name: s.studentName,
          },
          wetecResult: s.wetecResult,
        })),
      }),
    )
    commit('fetchedLessons', lessons)
  }),
  /**
   * レッスン詳細を取得（スケジュールAPI経由）
   *
   * @param {{
   *  scheduleId: string, // スケジュールID
   * }} param1
   */
  getLessonByScheduleId: (
    { commit, dispatch, getters },
    { scheduleId }: { scheduleId: string },
  ) => axios.get(`/schedules/${scheduleId}`).then(async ({ data }) => {
    const { tutor, schedule }: { tutor: any, schedule: any} = data
    const { type }: { type: scheduleType } = schedule

    if (type !== 'lesson' && type !== 'subLesson') {
      throw Error(`レッスンではないスケジュールIDでレッスン詳細を参照されました${scheduleId}`)
    }

    await Promise.all([
      dispatch('fetchMasterUniversities'),
      dispatch('fetchMasterCampuses'),
      dispatch('fetchMasterSemesters'),
      dispatch('fetchMasterLevels'),
      ..._.compact([
        tutor.tutorId,
        schedule?.substituteTutor?.tutorId,
      ]).map((tutorId) => dispatch('getTutor', { tutorId })),
      ...schedule.students.map(({ studentId }: { studentId: string }) => dispatch('getStudentById', { studentId })),
    ])

    const lesson: Lesson = {
      scheduleId,
      type,
      university: getters.findUniversityByCode(data?.schedule?.universityCode),
      campus: getters.findCampusByCode(data?.schedule?.campusCode),
      booth: {
        building: schedule.boothBuilding,
        room: schedule.boothRoom,
        number: schedule.boothNumber,
      },
      course: {
        name: schedule.courseName,
      },
      fiscalYear: schedule.fiscalYear,
      semester: getters.findSemesterByCode(schedule.semesterCode),
      unit: schedule.unit,
      date: moment(`${schedule.date}${schedule.startTime}`, 'YYYYMMDDhhmm'),
      teacher: {
        tutor: getters.findTutorById(tutor.tutorId),
        isAbsent: tutor.isVacation,
      },
      substituteTutor: schedule?.substituteTutor && {
        tutor: getters.findTutorById(schedule?.substituteTutor?.tutorId),
        time: schedule.substituteTutor.time,
        isTrainDelay: schedule.substituteTutor.isTrainDelay,
      },
      level: {
        name: data?.schedule?.level,
      },
      period: schedule.startPeriod,
      lessonMinute: schedule.lessonMinute,
      students: _.map(
        schedule.students,
        ({ studentId, wetecResult }): { student: Student, wetecResult: string } => ({
          student: getters.findStudentById(studentId),
          wetecResult: wetecResult as string,
        }),
      ),
    }

    commit('gotLesson', lesson)
  }),

  /**
   * 代講チューター登録
   * @param {Object} param0
   * @param {Object} param1 {
   *   lessonId: 代講対象レッスンのID, tutorId: 代講チューターのID, time: 代講時間 , isTrainDelay: 電車遅延フラグ
   * }
   */
  registerSubstitute: (
    context,
    {
      scheduleId,
      tutorId,
      time,
      isTrainDelay,
    }: {
      scheduleId: string,
      tutorId: string,
      time: number,
      isTrainDelay: boolean,
    },
  ) => axios.post(`/lessons/${scheduleId}/substitute`, {
    lessonId: scheduleId,
    tutorId,
    substituteTime: time || null,
    isTrainDelay: isTrainDelay ? 1 : 0,
  }),

  /**
   * 代講チューター削除
   * @param {{
   *  scheduleId: string, // レッスンのID
   * }}
   */
  removeSubstitute: (
    context,
    { scheduleId }: { scheduleId: string },
  ) => axios.delete(`/lessons/${scheduleId}/substitute`),
};

export default actions;
